import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useStripe } from '@stripe/react-stripe-js'

import { PUBSUB_GROUP_EVENT, ROUTES } from '@common'
import { Loader } from '@components'
import { StripeProvider, PubSubService, ErrorTrackingAPI } from '@services'
import { storageHelpers } from '@utils'
import { useCashRegister, useOrderRedux } from '@hooks'

const PAYMENT_STATUS = {
    CHECKING: 'checking',
    SUCCESS: 'success',
    FAILED: 'failed',
    ERROR: 'error',
}

const CheckPaymentPageInner = () => {
    const { orderId, clientSecret } = useParams()
    const stripe = useStripe()
    const navigate = useNavigate()
    const [countdown, setCountdown] = useState(2)
    const [status, setStatus] = useState(PAYMENT_STATUS.CHECKING)
    const [isLoading, setIsLoading] = useState(true)
    const { joinedGroupName } = useCashRegister()
    const { fetchOrderById } = useOrderRedux()
    const handleNavigateRef = useRef(null)
    const hasNavigatedRef = useRef(false)

    const handleNavigate = useCallback(async () => {
        const paymentOrigin = storageHelpers.getPaymentOrigin()
        storageHelpers.clearPaymentOrigin()

        if (paymentOrigin === 'checkout-assistant') {
            PubSubService.sendToGroup(joinedGroupName, { type: PUBSUB_GROUP_EVENT.PAYMENT_PAYNOW_SUCCESS })
            window.history.go(-1)
        } else if (paymentOrigin === 'advance-takeaway') {
            // If order is advance takeaway, navigate to member history with upcoming tab
            navigate(`${ROUTES.Order.history}?tab=upcoming`, { replace: true })
        } else {
            // Check if order is a preorder before navigating
            try {
                const res = await fetchOrderById(orderId)
                if (res.payload?.pickupScheduledAt) {
                    // If order is a preorder, navigate to member history with upcoming tab
                    navigate(`${ROUTES.Order.history}?tab=upcoming`, { replace: true })
                } else {
                    navigate(ROUTES.Order.complete.replace(':orderId', orderId), { replace: true, state: { isPaid: true } })
                }
            } catch (error) {
                // If error fetching order, fallback to OrderCompletePage
                ErrorTrackingAPI.sendError(error, 'CheckPaymentPage', 'fetchOrder')
                navigate(ROUTES.Order.complete.replace(':orderId', orderId), { replace: true, state: { isPaid: true } })
            }
        }
    }, [navigate, orderId, fetchOrderById, joinedGroupName])

    // Keep handleNavigate ref up to date
    useEffect(() => {
        handleNavigateRef.current = handleNavigate
    }, [handleNavigate])

    useEffect(() => {
        if (!stripe || !clientSecret) {
            return
        }

        let mounted = true

        const checkStatus = async () => {
            try {
                // Safety check: ensure body exists before calling Stripe API
                if (!document.body) {
                    if (mounted) {
                        setStatus(PAYMENT_STATUS.ERROR)
                    }
                    return
                }

                setIsLoading(true)

                const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret)
                if (mounted) {
                    if (paymentIntent && paymentIntent.status === 'succeeded') {
                        setStatus(PAYMENT_STATUS.SUCCESS)
                    } else {
                        setStatus(PAYMENT_STATUS.FAILED)
                    }
                }
            } catch (error) {
                if (mounted) {
                    setStatus(PAYMENT_STATUS.ERROR)
                }
            } finally {
                setIsLoading(false)
            }
        }

        checkStatus()
        return () => {
            mounted = false
        }
    }, [stripe, clientSecret])

    useEffect(() => {
        if (status !== PAYMENT_STATUS.SUCCESS) {
            return
        }

        // Stop countdown if navigation has already been triggered
        if (hasNavigatedRef.current) {
            return
        }

        const timer = setInterval(() => {
            setCountdown(prev => {
                // Stop countdown at 0
                if (prev <= 1) {
                    clearInterval(timer)
                    return 0
                }
                return prev - 1
            })
        }, 1000)

        return () => clearInterval(timer)
    }, [status])

    // Handle navigation when countdown reaches 0
    useEffect(() => {
        if (status === PAYMENT_STATUS.SUCCESS && countdown <= 0 && !hasNavigatedRef.current) {
            hasNavigatedRef.current = true
            // Use setTimeout to defer navigation after state update completes
            const timeoutId = setTimeout(() => {
                if (handleNavigateRef.current) {
                    handleNavigateRef.current()
                }
            }, 0)

            return () => clearTimeout(timeoutId)
        }
    }, [status, countdown])

    const renderContent = () => {
        switch (status) {
            case PAYMENT_STATUS.CHECKING:
                return (
                    <>
                        <h2 className="mb-4">Checking payment status...</h2>
                    </>
                )
            case PAYMENT_STATUS.SUCCESS:
                return (
                    <>
                        <h2 className="mb-4 text-primary">Payment Successful! 🎉</h2>
                        <p className="mb-4">Redirecting to order details in {countdown} seconds...</p>
                    </>
                )
            case PAYMENT_STATUS.FAILED:
                return (
                    <>
                        <h2 className="mb-4 text-danger">Payment failed! Please try again.</h2>
                        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
                            Back to Payment
                        </button>
                    </>
                )
            case PAYMENT_STATUS.ERROR:
                return (
                    <>
                        <h2 className="mb-4 text-danger">Something went wrong. Please try again.</h2>
                        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
                            Back to Payment
                        </button>
                    </>
                )
            default:
                return null
        }
    }

    return (
        <>
            {isLoading && <Loader />}
            <div className="text-center mt-5">{renderContent()}</div>
        </>
    )
}

const CheckPaymentPage = () => {
    return (
        <StripeProvider>
            <CheckPaymentPageInner />
        </StripeProvider>
    )
}

export default CheckPaymentPage
